import React, { createContext, useContext, useState, useEffect } from 'react';

// Crear contexto
const CookieContext = createContext();

// Proveedor del contexto
export const CookieProvider = ({ children }) => {
  // Estado para las preferencias de cookies
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true, // Las cookies esenciales siempre están activadas
    analytics: false,
    marketing: false,
  });
  
  // Estado para el control de los componentes
  const [cookiesAccepted, setCookiesAccepted] = useState(false);
  const [cookieSettingsOpen, setCookieSettingsOpen] = useState(false);
  
  // Cargar preferencias guardadas al iniciar
  useEffect(() => {
    const loadSavedPreferences = () => {
      // Verificar si ya se ha aceptado alguna vez
      const accepted = getCookie('cookies_accepted');
      if (accepted) {
        setCookiesAccepted(true);
        
        // Cargar las preferencias guardadas
        const savedPrefs = getCookie('cookie_preferences');
        if (savedPrefs) {
          try {
            const parsed = JSON.parse(savedPrefs);
            // Asegurar que essential siempre sea true
            setCookiePreferences({ ...parsed, essential: true });
          } catch (error) {
            console.error('Error parsing cookie preferences:', error);
          }
        }
      }
    };
    
    loadSavedPreferences();
  }, []);
  
  // Función para actualizar una preferencia específica
  const updateCookiePreference = (type, value) => {
    setCookiePreferences(prev => ({
      ...prev,
      [type]: value,
    }));
  };
  
  // Función para aceptar todas las cookies
  const acceptAllCookies = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    
    setCookiePreferences(allAccepted);
    savePreferencesToCookies(allAccepted);
    setCookiesAccepted(true);
    setCookieSettingsOpen(false);
  };
  
  // Función para guardar las preferencias actuales
  const savePreferences = () => {
    savePreferencesToCookies(cookiePreferences);
    setCookiesAccepted(true);
    setCookieSettingsOpen(false);
  };
  
  // Guardar preferencias en cookies
  const savePreferencesToCookies = (preferences) => {
    // Guardar que se han aceptado las cookies
    setCookie('cookies_accepted', 'true', 365);
    
    // Guardar las preferencias específicas
    setCookie('cookie_preferences', JSON.stringify(preferences), 365);
    
    // Aplicar las preferencias (cargar scripts según configuración)
    applyPreferences(preferences);
  };
  
  // Aplicar las preferencias configuradas
  const applyPreferences = (preferences) => {
    // Actualizar Google Consent Mode según las preferencias
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': preferences.analytics ? 'granted' : 'denied',
        'ad_storage': preferences.marketing ? 'granted' : 'denied',
        'functionality_storage': preferences.essential ? 'granted' : 'denied',
        'personalization_storage': preferences.marketing ? 'granted' : 'denied',
        'security_storage': 'granted' // Siempre permitido por razones de seguridad
      });
    }
    
    // Lógica para aplicar scripts según las preferencias
    if (preferences.analytics) {
      loadAnalytics();
    }
    
    if (preferences.marketing) {
      loadMarketing();
    }
    
    // Disparar un evento personalizado para notificar a otros scripts
    document.dispatchEvent(new CustomEvent('cookiesConfigured', { 
      detail: preferences 
    }));
  };
  
  // Funciones para cargar scripts externos
  const loadAnalytics = () => {
    // Aquí iría el código para cargar analytics (como Google Analytics)
    console.log('Analytics cookies activated');
    // Ejemplo:
    // if (typeof window.dataLayer === 'undefined') {
    //   window.dataLayer = window.dataLayer || [];
    //   function gtag(){dataLayer.push(arguments);}
    //   gtag('js', new Date());
    //   gtag('config', 'G-XXXXXXXXXX');
    //   
    //   const script = document.createElement('script');
    //   script.src = 'https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX';
    //   script.async = true;
    //   document.head.appendChild(script);
    // }
  };
  
  const loadMarketing = () => {
    // Aquí iría el código para cargar herramientas de marketing
    console.log('Marketing cookies activated');
    // Ejemplo:
    // if (typeof window.fbq === 'undefined') {
    //   !function(f,b,e,v,n,t,s){...}(window, document, 'script',
    //     'https://connect.facebook.net/en_US/fbevents.js');
    //   fbq('init', 'XXXXXXXXXX');
    //   fbq('track', 'PageView');
    // }
  };
  
  // Funciones para abrir/cerrar los ajustes de cookies
  const openCookieSettings = () => {
    setCookieSettingsOpen(true);
  };
  
  const closeCookieSettings = () => {
    setCookieSettingsOpen(false);
  };
  
  // Utilidades para gestionar cookies
  function setCookie(name, value, days) {
    let expires = '';
    
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = '; expires=' + date.toUTCString();
    }
    
    document.cookie = name + '=' + (value || '') + expires + '; path=/; SameSite=Lax';
  }
  
  function getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }
      
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length, c.length);
      }
    }
    
    return null;
  }
  
  // Función para eliminar cookies (no utilizada actualmente pero disponible para futuros desarrollos)
  /*
  function deleteCookie(name) {
    document.cookie = name + '=; Max-Age=-99999999; path=/';
  }
  */
  
  // Proporcionar el contexto
  const value = {
    cookiePreferences,
    cookiesAccepted,
    cookieSettingsOpen,
    updateCookiePreference,
    acceptAllCookies,
    savePreferences,
    openCookieSettings,
    closeCookieSettings,
  };
  
  return <CookieContext.Provider value={value}>{children}</CookieContext.Provider>;
};

// Hook personalizado para usar el contexto
export const useCookies = () => {
  const context = useContext(CookieContext);
  if (context === undefined) {
    throw new Error('useCookies debe ser usado dentro de un CookieProvider');
  }
  return context;
};
